import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, AlertTriangle, CheckCircle2, ShieldQuestion, Loader2, Fuel,
} from "lucide-react";

const VERDICT_META = {
  no_new_damage: { color: "bg-green-50 border-green-300 text-green-800", icon: CheckCircle2 },
  new_damage_detected: { color: "bg-rose-50 border-rose-300 text-rose-800", icon: AlertTriangle },
  inconclusive: { color: "bg-amber-50 border-amber-300 text-amber-800", icon: ShieldQuestion },
};

const AIInspector = forwardRef(function AIInspector({ rentalId, onChargeApplied }, ref) {
  const { t, fmtCurrency } = useT();
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [modalAi, setModalAi] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/rentals/${rentalId}/ai-inspections`);
      setItems(r.data || []);
      return r.data || [];
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (rentalId) load();
  }, [rentalId]);

  const runInspection = async ({ showModal = false } = {}) => {
    if (running) return null;
    setRunning(true);
    try {
      const r = await api.post(`/rentals/${rentalId}/ai-inspect`);
      toast.success(t("aiInspector.completed"));
      await load();
      if (showModal && r.data) setModalAi(r.data);
      return r.data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("aiInspector.failed"));
      return null;
    } finally {
      setRunning(false);
    }
  };

  // Expose imperative API to parent (RentalDetails)
  useImperativeHandle(ref, () => ({
    triggerAuto: async () => {
      // Only trigger if no inspection exists yet
      const existing = await load();
      if ((existing || []).length === 0) {
        return runInspection({ showModal: true });
      }
      return null;
    },
  }), [rentalId, running]);

  const applyCharge = async (aiId) => {
    if (!window.confirm(t("aiInspector.confirmApply"))) return;
    try {
      await api.post(`/rentals/${rentalId}/ai-inspections/${aiId}/apply-charge`);
      toast.success(t("aiInspector.chargeApplied"));
      onChargeApplied?.();
      setModalAi(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="border border-zinc-200 bg-white p-4 space-y-3" data-testid="ai-inspector">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline mb-1">{t("aiInspector.title")}</div>
            <p className="text-xs text-zinc-500 max-w-prose">{t("aiInspector.subtitle")}</p>
          </div>
          <Button
            onClick={() => runInspection({ showModal: true })}
            disabled={running}
            className="rounded-none bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="run-ai-inspect-button"
          >
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {running ? t("aiInspector.analyzing") : t("aiInspector.runBtn")}
          </Button>
        </div>

        {items.length === 0 && !running && (
          <div className="text-xs text-zinc-500 italic">{t("aiInspector.empty")}</div>
        )}

        {items.map((ai) => (
          <AIResultCard
            key={ai.id}
            ai={ai}
            onApply={() => applyCharge(ai.id)}
            onOpen={() => setModalAi(ai)}
            t={t}
            fmtCurrency={fmtCurrency}
            compact
          />
        ))}
      </div>

      {/* Auto-modal after analysis */}
      <Dialog open={!!modalAi} onOpenChange={(o) => !o && setModalAi(null)}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              {t("aiInspector.modalTitle")}
            </DialogTitle>
          </DialogHeader>
          {modalAi && (
            <div className="space-y-4">
              <AIResultCard ai={modalAi} t={t} fmtCurrency={fmtCurrency} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAi(null)} className="rounded-none">
              {t("aiInspector.reviewLater")}
            </Button>
            {modalAi && modalAi.total_estimated_charge_usd > 0 && !modalAi.applied_charge_ids?.length && (
              <Button
                onClick={() => applyCharge(modalAi.id)}
                className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="modal-apply-charge-button"
              >
                {t("aiInspector.applyChargeTotal", { amount: fmtCurrency(modalAi.total_estimated_charge_usd) })}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default AIInspector;

function AIResultCard({ ai, onApply, t, fmtCurrency, compact }) {
  const meta = VERDICT_META[ai.verdict] || VERDICT_META.inconclusive;
  const Icon = meta.icon;
  const fuel = ai.fuel || {};
  const showFuel = fuel.pickup_pct != null || fuel.return_pct != null;
  const damage = Number(ai.damage_estimated_charge_usd || 0);
  const fuelCharge = Number(fuel.estimated_charge_usd || 0);
  const totalCharge = Number(ai.total_estimated_charge_usd || 0);

  return (
    <div className={`border ${meta.color} p-4 space-y-3`} data-testid="ai-inspection-result">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" />
        <div className="font-semibold text-sm">
          {t(`aiInspector.verdict.${ai.verdict || "inconclusive"}`)}
        </div>
        <div className="text-xs ml-auto font-mono opacity-70">
          {t("aiInspector.confidence")}: {ai.confidence_pct ?? 0}%
        </div>
      </div>

      {ai.summary && <div className="text-xs leading-relaxed">{ai.summary}</div>}

      {showFuel && (
        <div className="bg-white/70 border border-current/20 p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <Fuel className="w-4 h-4" /> {t("aiInspector.fuel.title")}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="overline text-[10px] opacity-70">{t("aiInspector.fuel.pickup")}</div>
              <div className="font-mono font-bold">{fuel.pickup_pct ?? "—"}%</div>
            </div>
            <div>
              <div className="overline text-[10px] opacity-70">{t("aiInspector.fuel.return")}</div>
              <div className="font-mono font-bold">{fuel.return_pct ?? "—"}%</div>
            </div>
            <div>
              <div className="overline text-[10px] opacity-70">{t("aiInspector.fuel.missing")}</div>
              <div className="font-mono font-bold text-rose-700">{fuel.missing_pct ?? 0}%</div>
            </div>
          </div>
          {fuelCharge > 0 && (
            <div className="text-xs pt-1 border-t border-current/20">
              {t("aiInspector.fuel.calc", {
                tank: fuel.tank_gal,
                price: fuel.price_usd_per_gal,
                charge: fmtCurrency(fuelCharge),
              })}
            </div>
          )}
        </div>
      )}

      {(ai.items || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="overline text-[10px] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {t("aiInspector.damages")}
          </div>
          {ai.items.map((it, idx) => (
            <div key={idx} className="text-xs border-l-2 border-current/30 pl-2 py-0.5 flex items-start gap-2">
              <div className="flex-1">
                <span className="font-mono uppercase opacity-60">{it.area}</span>
                <span className="mx-1">·</span>
                <span className="font-semibold">{it.issue_type}</span>
                <span className="mx-1">·</span>
                <span>{it.severity}</span>
                <div className="opacity-80">{it.description}</div>
              </div>
              <div className="font-mono text-right shrink-0">
                ${(it.estimated_charge_usd || 0).toFixed(2)}
                <div className="opacity-60">{it.confidence_pct || 0}%</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalCharge > 0 && (
        <div className="flex items-center justify-between pt-2 border-t border-current/20">
          <div className="text-sm">
            <span className="overline">{t("aiInspector.suggestedCharge")}: </span>
            <span className="font-semibold font-mono">{fmtCurrency(totalCharge)}</span>
            {damage > 0 && fuelCharge > 0 && (
              <span className="text-[10px] font-mono opacity-60 ml-2">
                ({t("aiInspector.fuel.damageLabel")} {fmtCurrency(damage)} + {t("aiInspector.fuel.fuelLabel")} {fmtCurrency(fuelCharge)})
              </span>
            )}
          </div>
          {compact && onApply && (
            ai.applied_charge_ids?.length || ai.applied_charge_id ? (
              <div className="text-xs italic opacity-70">{t("aiInspector.alreadyApplied")}</div>
            ) : (
              <Button
                size="sm"
                onClick={onApply}
                className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="apply-ai-charge-button"
              >
                {t("aiInspector.applyCharge")}
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}
